import { useContext, useEffect, useState } from 'react'
import { getLocale } from '../components/CurrencyConverterSwitcher'
import { LayoutContext } from '../contexts'
import * as R from 'ramda'

export const useFetchReviews = ({ family }) => {
  const { node_locale } = useContext(LayoutContext)
  const locale = getLocale(node_locale)

  const [count, setCount] = useState(0)
  const [score, setScore] = useState(null)
  const [reviews, setReviews] = useState([])
  const [reviewCount, setReviewCount] = useState(3)
  const [clicks, setClicks] = useState(1)
  const [maxClicks, setMaxClicks] = useState()

  const fetchFrAndEnReviews = async (
    skus,
    setCount,
    setReviews,
    setMaxClicks,
    setClicks,
    setScore,
    node_locale,
  ) => {
    const base =
      'https://api.trustpilot.com/v1/product-reviews/business-units/55bf22080000ff000581b7e9'
    const apikey = 'yPwGMsdo8dG5IAcZ8hAZGy5o9VjXhFg0'
    const sku = skus?.join(',')

    const language = /-/.test(node_locale)
      ? node_locale.split('-')[0]
      : node_locale

    const summaryUrl = new URL(base)
    summaryUrl.search = new URLSearchParams({ apikey, sku })

    const frReviewsUrl = new URL(`${base}/reviews`)
    frReviewsUrl.search = new URLSearchParams({
      apikey,
      sku,
      language,
      perPage: 100,
    })

    const enReviewsUrl = new URL(`${base}/reviews`)
    enReviewsUrl.search = new URLSearchParams({
      apikey,
      sku,
      language: 'en',
      perPage: 100,
    })

    const [summaryResponse, enReviewsResponse, frReviewsResponse] =
      await Promise.all([
        fetch(summaryUrl),
        fetch(enReviewsUrl),
        fetch(frReviewsUrl),
      ])

    const [summaryData, enReviewsData, frReviewsData] = await Promise.all([
      summaryResponse.json(),
      enReviewsResponse.json(),
      frReviewsResponse.json(),
    ])

    setScore(summaryData.starsAverage)
    setCount(summaryData.numberOfReviews.total)
    setReviews([
      ...enReviewsData.productReviews,
      ...frReviewsData.productReviews,
    ])
    setMaxClicks(
      Math.floor(
        summaryData.numberOfReviews.total / 3 > 33
          ? 33
          : summaryData.numberOfReviews.total / 3,
      ),
    )
    setClicks(1)
  }

  const fetchTrustPilotReviews = async (
    skus,
    setCount,
    setReviews,
    setMaxClicks,
    setClicks,
    setScore,
    node_locale,
  ) => {
    const base =
      'https://api.trustpilot.com/v1/product-reviews/business-units/55bf22080000ff000581b7e9'
    const apikey = 'yPwGMsdo8dG5IAcZ8hAZGy5o9VjXhFg0'
    const sku = skus?.join(',')

    const language = /-/.test(node_locale)
      ? node_locale.split('-')[0]
      : node_locale

    const summaryUrl = new URL(base)
    summaryUrl.search = new URLSearchParams({ apikey, sku })

    const reviewsUrl = new URL(`${base}/reviews`)
    reviewsUrl.search = new URLSearchParams({
      apikey,
      sku,
      language,
      perPage: 100,
    })

    const [summaryResponse, reviewsResponse] = await Promise.all([
      fetch(summaryUrl),
      fetch(reviewsUrl),
    ])

    const [summaryData, reviewsData] = await Promise.all([
      summaryResponse.json(),
      reviewsResponse.json(),
    ])

    setScore(summaryData.starsAverage)
    setCount(summaryData.numberOfReviews.total)
    setReviews(R.uniqWith(R.eqProps('consumer'))(reviewsData.productReviews)) // filters by the same consumer to prevent duplicates
    setMaxClicks(
      Math.floor(
        summaryData.numberOfReviews.total / 3 > 33
          ? 33
          : summaryData.numberOfReviews.total / 3,
      ),
    )
    setClicks(1)
  }

  const fetchTrustedShopsReviews = async (
    skus,
    setCount,
    setReviews,
    setMaxClicks,
    setClicks,
    setScore,
  ) => {
    try {
      const url = `https://stoxv2.netlify.app/.netlify/functions/trusted_shops`

      const response = await fetch(encodeURI(url), { method: 'GET' })

      if (!response.ok) {
        return {
          statusCode: response.status,
          body: response.statusText,
        }
      }

      const {
        metrics: {
          response: {
            data: {
              shop: {
                qualityIndicators: {
                  reviewIndicator: { totalReviewCount, overallMark },
                },
              },
            },
          },
        },
        productReviews: {
          response: {
            data: {
              shop: { productReviews },
            },
          },
        },
      } = await response.json()

      const familyReviews = productReviews.filter(
        (review) =>
          skus.includes(review.product.sku) && Number(review.mark ?? 5) > 1,
      )

      setScore(overallMark)
      setCount(totalReviewCount)
      setReviews(familyReviews)
      setMaxClicks(
        Math.floor(
          familyReviews.length / 3 > 33 ? 33 : familyReviews.length / 3,
        ),
      )
      setClicks(1)
    } catch (error) {
      setCount(0)
      setScore(0)
      setReviews([])
      setMaxClicks(0)
      setClicks(1)
      console.error(error)
    }
  }
  useEffect(() => {
    const locale = getLocale(node_locale)
    if (locale === 'fr') {
      // client wants fr + en reviews for fr site
      fetchFrAndEnReviews(
        family.legacySkus,
        setCount,
        setReviews,
        setMaxClicks,
        setClicks,
        setScore,
        locale,
      )
      // console.log(' i am FR&EN reviews and i got fired')
    } else if (locale === 'de') {
      fetchTrustedShopsReviews(
        family.legacySkus,
        setCount,
        setReviews,
        setMaxClicks,
        setClicks,
        setScore,
      )
      // console.log(' i am trusted shops reviews and i got fired')
    } else {
      fetchTrustPilotReviews(
        family.legacySkus,
        setCount,
        setReviews,
        setMaxClicks,
        setClicks,
        setScore,
        locale,
      )
      // console.log(' i am truspiolt reviews and i got fired')
    }
  }, [])

  return {
    count,
    reviews,
    score,
    maxClicks,
    clicks,
    reviewCount,
  }
}
